// src/here/config.ts
var apiKey;
function initHereApi(key) {
  apiKey = key;
}
function getHereApiKey() {
  if (!apiKey) {
    throw new Error("HERE API not initialized. Call initHereApi() first.");
  }
  return apiKey;
}

// src/here/discover.ts
var HERE_SEARCH_COUNTRIES = [
  "ALB",
  "AND",
  "AUT",
  "BLR",
  "BEL",
  "BIH",
  "BGR",
  "HRV",
  "CYP",
  "CZE",
  "DNK",
  "EST",
  "FIN",
  "FRA",
  "DEU",
  "GRC",
  "HUN",
  "ISL",
  "IRL",
  "ITA",
  "XKX",
  "LVA",
  "LIE",
  "LTU",
  "LUX",
  "MLT",
  "MDA",
  "MCO",
  "MNE",
  "NLD",
  "MKD",
  "NOR",
  "POL",
  "PRT",
  "ROU",
  "SMR",
  "SRB",
  "SVK",
  "SVN",
  "ESP",
  "SWE",
  "CHE",
  "UKR",
  "GBR",
  "VAT",
  "RUS",
  "TUR"
];
var HERE_SEARCH_FOCUS = "48.2,16.4";
async function discoverAddresses(params) {
  const apiKey2 = getHereApiKey();
  const countries = params.countries ?? HERE_SEARCH_COUNTRIES;
  const url = new URL("https://discover.search.hereapi.com/v1/discover");
  url.searchParams.set("apikey", apiKey2);
  url.searchParams.set("q", params.query);
  url.searchParams.set("at", params.at ?? HERE_SEARCH_FOCUS);
  url.searchParams.set("in", `countryCode:${countries.join(",")}`);
  url.searchParams.set("limit", String(params.limit ?? 8));
  url.searchParams.set("lang", params.lang ?? "en");
  const response = await fetch(url.toString());
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.title ?? `HERE API error: ${response.status}`);
  }
  const data = await response.json();
  const items = [];
  for (const item of data.items ?? []) {
    if (item.position && item.address?.label) {
      items.push({
        id: item.id ?? item.address.label,
        label: item.address.label,
        lat: item.position.lat,
        lng: item.position.lng
      });
    }
  }
  return items;
}

// src/here/routing.ts
async function calculateRouteTolls(params) {
  const apiKey2 = getHereApiKey();
  const url = new URL("https://router.hereapi.com/v8/routes");
  url.searchParams.set("apikey", apiKey2);
  const mode = params.transportMode ?? "truck";
  url.searchParams.set("transportMode", mode);
  url.searchParams.set("origin", `${params.origin.lat},${params.origin.lng}`);
  url.searchParams.set(
    "destination",
    `${params.destination.lat},${params.destination.lng}`
  );
  url.searchParams.set("return", "travelSummary,tolls,polyline");
  url.searchParams.set("spans", "countryCode,length");
  url.searchParams.set("currency", params.currency ?? "EUR");
  if (params.departureTime) {
    url.searchParams.set("departureTime", params.departureTime);
  }
  if (params.via && params.via.length > 0) {
    for (const point of params.via) {
      url.searchParams.append("via", `${point.lat},${point.lng}`);
    }
  }
  const avoidFeatures = [];
  if (params.avoidHighway) avoidFeatures.push("controlledAccessHighway");
  if (params.avoidToll) avoidFeatures.push("tollRoad");
  if (avoidFeatures.length > 0) {
    url.searchParams.set("avoid[features]", avoidFeatures.join(","));
  }
  if (params.alternatives) {
    url.searchParams.set("alternatives", String(params.alternatives));
  }
  if (params.truckParams) {
    const tp = params.truckParams;
    if (tp.axleCount)
      url.searchParams.set("vehicle[axleCount]", String(tp.axleCount));
    if (tp.height)
      url.searchParams.set(
        "vehicle[height]",
        String(Math.round(tp.height * 100))
      );
    if (tp.width)
      url.searchParams.set("vehicle[width]", String(Math.round(tp.width * 100)));
    if (tp.length)
      url.searchParams.set(
        "vehicle[length]",
        String(Math.round(tp.length * 100))
      );
    if (tp.grossWeight)
      url.searchParams.set("vehicle[grossWeight]", String(tp.grossWeight));
    if (tp.weightPerAxle)
      url.searchParams.set("vehicle[weightPerAxle]", String(tp.weightPerAxle));
    if (tp.speedCap)
      url.searchParams.set(
        "vehicle[speedCap]",
        String(Math.round(tp.speedCap / 3.6))
      );
    if (tp.shippedHazardousGoods && tp.shippedHazardousGoods.length > 0) {
      for (const goods of tp.shippedHazardousGoods) {
        url.searchParams.append("vehicle[shippedHazardousGoods]", goods);
      }
    }
    if (tp.emissionType)
      url.searchParams.set("tolls[emissionType]", tp.emissionType);
  }
  const response = await fetch(url.toString());
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.title ?? `HERE API error: ${response.status}`);
  }
  const data = await response.json();
  const routes = data.routes ?? [];
  if (routes.length === 0) {
    throw new Error("No route found");
  }
  return routes.map(
    (route) => parseRouteResponse(route, params.currency ?? "EUR")
  );
}
function parseRouteResponse(route, currency) {
  let totalDistance = 0;
  let estimatedDuration = 0;
  const tollsByCountry = /* @__PURE__ */ new Map();
  let departureTime = "";
  const polylines = [];
  for (const section of route.sections ?? []) {
    if (section.polyline) {
      polylines.push(section.polyline);
    }
    const summary = section.travelSummary;
    if (summary) {
      totalDistance += summary.length ?? 0;
      estimatedDuration += summary.duration ?? 0;
    }
    if (section.departure?.time) {
      departureTime = section.departure.time;
    }
    for (const toll of section.tolls ?? []) {
      const countryCode = toll.countryCode ?? "UNKNOWN";
      const tollSystem = toll.tollSystem ?? "";
      const fare = toll.fares?.find((f) => !f.pass) ?? toll.fares?.[0];
      const price = fare?.convertedPrice?.value ?? fare?.price?.value ?? 0;
      const existing = tollsByCountry.get(countryCode);
      if (existing) {
        existing.price += price;
      } else {
        tollsByCountry.set(countryCode, {
          price,
          tollSystem
        });
      }
    }
  }
  const distanceByCountry = /* @__PURE__ */ new Map();
  for (const section of route.sections ?? []) {
    for (const span of section.spans ?? []) {
      if (span.countryCode && span.length) {
        const existing = distanceByCountry.get(span.countryCode) ?? 0;
        distanceByCountry.set(span.countryCode, existing + span.length);
      }
    }
  }
  const tolls = [];
  let totalTollCost = 0;
  for (const [countryCode, data] of tollsByCountry) {
    tolls.push({
      countryCode,
      tollSystem: data.tollSystem,
      price: data.price,
      currency,
      distance: distanceByCountry.get(countryCode) ?? 0
    });
    totalTollCost += data.price;
  }
  for (const [countryCode, distance] of distanceByCountry) {
    if (!tollsByCountry.has(countryCode)) {
      tolls.push({ countryCode, tollSystem: "", price: 0, currency, distance });
    }
  }
  return {
    totalDistance,
    estimatedDuration,
    tolls,
    totalTollCost,
    tollCurrency: currency,
    departureTime,
    polylines
  };
}
export {
  calculateRouteTolls,
  discoverAddresses,
  getHereApiKey,
  initHereApi
};
//# sourceMappingURL=here.mjs.map